// Auth Context - manages user authentication state across the entire app
// This uses React Context API to share auth state with all components

import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../utils/api';

// Create the context
const AuthContext = createContext(null);

// Custom hook to use auth context
// This makes it easy to access auth state in any component
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

// Auth Provider component
// Wrap your app with this to provide auth state to all components
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    // Check if user is already logged in when app loads
    useEffect(() => {
        checkAuth();
    }, []);

    // Check if there's a valid token in localStorage
    const checkAuth = async () => {
        const token = localStorage.getItem('token');
        const savedUser = localStorage.getItem('user');

        if (token && savedUser) {
            try {
                // Verify the token is still valid
                const response = await authAPI.verifyToken();
                setUser(response.user);
                setIsAuthenticated(true);
            } catch (error) {
                // Token is invalid, clear everything
                logout();
            }
        }
        
        setLoading(false);
    };

    // Login function
    const login = async (username, password) => {
        try {
            const response = await authAPI.login(username, password);
            
            // Save token and user to localStorage
            localStorage.setItem('token', response.token);
            localStorage.setItem('user', JSON.stringify(response.user));
            
            // Update state
            setUser(response.user);
            setIsAuthenticated(true);
            
            return { success: true };
        } catch (error) {
            const message = error.response?.data?.message || 'Login failed';
            return { success: false, message };
        }
    };

    // Logout function
    const logout = () => {
        // Clear localStorage
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        // Clear state
        setUser(null);
        setIsAuthenticated(false);
    };

    // Value provided to all components
    const value = {
        user,
        isAuthenticated,
        loading,
        login,
        logout
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};